import React,{useState,useEffect,useMemo} from 'react';
import {motion,AnimatePresence} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import VideoCard from './VideoCard';

export const HighlightText=({text,query})=> {
if (!query) return <span>{text}</span>;
const parts=text.split(new RegExp(`(${query.split(' ').join('|')})`,'gi'));
return (
<span>
{parts.map((part,i)=>
query.toLowerCase().split(' ').includes(part.toLowerCase()) ? (
<mark key={i} className="bg-blue-100 text-blue-900 px-0.5 rounded transition-colors">{part}</mark>
) : (
<span key={i}>{part}</span>
)
)}
</span>
);
};

export function CommandPalette({isOpen,onClose,demos,onSelect}) {
const [query,setQuery]=useState('');
const [selectedIndex,setSelectedIndex]=useState(0);

const filtered=useMemo(()=> {
if (!query) return [];
const tokens=query.toLowerCase().split(' ').filter(t=> t.length > 0);
return demos
.map(demo=> {
let score=0;
const searchable=`${demo.title} ${demo.tags.join(' ')} ${demo.deliverables.join(' ')} ${demo.keywords?.join(' ') || ''} ${demo.description} ${(demo.bullets || []).join(' ')}`.toLowerCase();
const allTokensMatch=tokens.every(token=> searchable.includes(token));
if (!allTokensMatch) return {...demo,score: 0};
if (demo.title.toLowerCase().includes(query.toLowerCase())) score +=10;
tokens.forEach(t=> {
if (demo.title.toLowerCase().includes(t)) score +=5;
if (demo.tags.some(tag=> tag.toLowerCase().includes(t))) score +=4;
if (demo.deliverables.some(d=> d.toLowerCase().includes(t))) score +=4;
});
return {...demo,score};
})
.filter(d=> d.score > 0)
.sort((a,b)=> b.score - a.score)
.slice(0,6);
},[query,demos]);

useEffect(()=> {
const handleKeyDown=(e)=> {
if (e.key==='ArrowDown') {
e.preventDefault();
setSelectedIndex(prev=> Math.min(prev + 1,filtered.length - 1));
} else if (e.key==='ArrowUp') {
e.preventDefault();
setSelectedIndex(prev=> Math.max(prev - 1,0));
} else if (e.key==='Enter' && filtered[selectedIndex]) {
onSelect(filtered[selectedIndex]);
} else if (e.key==='Escape') {
onClose();
}
};
if (isOpen) window.addEventListener('keydown',handleKeyDown);
return ()=> window.removeEventListener('keydown',handleKeyDown);
},[isOpen,filtered,selectedIndex,onSelect,onClose]);

if (!isOpen) return null;

return (
<div className="fixed inset-0 z-[200] flex items-start justify-center pt-[15vh] px-4">
<motion.div
initial={{opacity: 0}}
animate={{opacity: 1}}
exit={{opacity: 0}}
className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm"
onClick={onClose}
/>
<motion.div
initial={{scale: 0.95,opacity: 0}}
animate={{scale: 1,opacity: 1}}
className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
>
<div className="flex items-center px-6 py-4 border-b border-slate-100">
<SafeIcon icon={FiIcons.FiSearch} className="w-5 h-5 text-slate-400 mr-4" />
<input
autoFocus
className="flex-1 bg-transparent border-none outline-none text-lg text-slate-900 placeholder-slate-400"
placeholder="Quick search demos..."
value={query}
onChange={(e)=> {setQuery(e.target.value);setSelectedIndex(0);}}
/>
<div className="flex items-center gap-1">
<kbd className="px-2 py-1 bg-slate-100 rounded text-[10px] text-slate-500 font-sans border border-slate-200">ESC</kbd>
</div>
</div>
<div className="max-h-[60vh] overflow-y-auto p-2">
{filtered.length > 0 ? (
<div className="space-y-1">
{filtered.map((demo,idx)=> (
<button
key={demo.id}
onClick={()=> onSelect(demo)}
onMouseEnter={()=> setSelectedIndex(idx)}
className={`w-full text-left px-4 py-3 rounded-xl flex items-center gap-4 transition-colors ${idx===selectedIndex ? 'bg-slate-50' : ''}`}
>
<div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600 shrink-0">
<SafeIcon icon={demo.category==='automation' ? FiIcons.FiCpu : FiIcons.FiBarChart2} className="w-5 h-5" />
</div>
<div className="flex-1 min-w-0">
<div className="text-sm font-bold text-slate-900">
<HighlightText text={demo.title} query={query} />
</div>
<div className="text-[11px] text-slate-500 truncate">
{demo.deliverables.join(' • ')}
</div>
</div>
<SafeIcon icon={FiIcons.FiCornerDownEnter} className={`w-4 h-4 text-slate-300 ${idx===selectedIndex ? 'opacity-100' : 'opacity-0'}`} />
</button>
))}
</div>
) : query ? (
<div className="py-12 text-center text-slate-400 text-sm">No results found for "{query}"</div>
) : (
<div className="py-12 text-center text-slate-400 text-sm">Type to search by keyword,deliverable,or outcome</div>
)}
</div>
</motion.div>
</div>
);
}

export function DetailsModal({demo,isOpen,onClose,onShortlist,isShortlisted}) {
const [showVideoModal,setShowVideoModal]=useState(false);

useEffect(()=> {
if (!isOpen) setShowVideoModal(false);
},[isOpen]);

if (!demo || !isOpen) return null;

return (
<>
<AnimatePresence>
<div className="fixed inset-0 z-[150] flex items-center justify-center p-4">
<motion.div
initial={{opacity: 0}}
animate={{opacity: 1}}
exit={{opacity: 0}}
className="fixed inset-0 bg-slate-950/60 backdrop-blur-md"
onClick={onClose}
/>
<motion.div
initial={{y: 50,opacity: 0}}
animate={{y: 0,opacity: 1}}
exit={{y: 50,opacity: 0}}
className="relative w-full max-w-4xl bg-white rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
>
<button
onClick={onClose}
className="absolute top-6 right-6 z-10 p-2 rounded-full bg-black/5 hover:bg-black/10 transition-colors"
>
<SafeIcon icon={FiIcons.FiX} className="w-6 h-6" />
</button>

<div className="overflow-y-auto">
<div className="grid grid-cols-1 md:grid-cols-2">
<div className="p-8 md:p-12">
<div className="flex items-center gap-3 mb-6">
<span className="px-3 py-1 bg-blue-50 text-blue-600 text-[10px] font-bold uppercase tracking-widest rounded-full">{demo.category}</span>
<button
onClick={()=> onShortlist(demo.id)}
className={`flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full border transition-all ${isShortlisted ? 'bg-amber-50 border-amber-200 text-amber-600' : 'bg-slate-50 border-slate-200 text-slate-500'}`}
>
<SafeIcon icon={isShortlisted ? FiIcons.FiStar : FiIcons.FiPlus} className="w-3 h-3" />
{isShortlisted ? 'Shortlisted' : 'Shortlist'}
</button>
</div>

<h2 className="text-3xl font-bold text-slate-900 mb-6 leading-tight">{demo.title}</h2>
<p className="text-slate-600 mb-8 leading-relaxed">{demo.description}</p>

<div className="space-y-6">
<div>
<h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Deliverables</h4>
<div className="flex flex-wrap gap-2">
{demo.deliverables.map(d=> (
<span key={d} className="px-3 py-1 bg-slate-50 text-slate-600 text-xs rounded-lg border border-slate-100">{d}</span>
))}
</div>
</div>
<div>
<h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Key Outcomes</h4>
<ul className="space-y-2">
{demo.bullets.map((b,i)=> (
<li key={i} className="flex items-start gap-3 text-sm text-slate-600">
<SafeIcon icon={FiIcons.FiCheck} className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
{b}
</li>
))}
</ul>
</div>
</div>

<div className="flex flex-col gap-4 mt-12">
{demo.videoUrl && (
<button
onClick={()=> setShowVideoModal(true)}
className="w-full bg-blue-600 text-white text-center py-4 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-blue-700 transition-colors shadow-lg"
>
<SafeIcon icon={FiIcons.FiPlay} className="w-5 h-5" />
{demo.actionLabel || "Watch Demo"}
</button>
)}
{demo.liveUrl && (
<a
href={demo.liveUrl}
target="_blank"
rel="noopener noreferrer"
className="w-full bg-blue-600 text-white text-center py-4 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-blue-700 transition-colors shadow-lg"
>
<SafeIcon icon={FiIcons.FiExternalLink} className="w-5 h-5" />
{demo.actionLabel || "Open Live Demo"}
</a>
)}
<div className="flex flex-col sm:flex-row gap-4">
<a
href="#enquiry-form"
onClick={onClose}
className="flex-1 bg-slate-900 text-white text-center py-4 rounded-xl font-bold hover:bg-slate-800 transition-colors"
>
Send Enquiry
</a>
<a
href={`https://wa.me/61425228778?text=Hello,I'm interested in ${demo.title}`}
target="_blank"
className="flex-1 bg-[#25D366] text-white text-center py-4 rounded-xl font-bold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
>
<SafeIcon icon={FaIcons.FaWhatsapp} className="w-5 h-5" />
WhatsApp
</a>
</div>
</div>
</div>

<div className="bg-slate-50 p-4 flex items-center justify-center">
<VideoCard demo={demo} isModal />
</div>
</div>
</div>
</motion.div>
</div>
</AnimatePresence>

{showVideoModal && demo.videoUrl && (
<div
className="fixed inset-0 z-[200] bg-slate-950/90 backdrop-blur-md flex items-center justify-center p-4 sm:p-8"
onClick={()=> setShowVideoModal(false)}
>
<button
className="absolute top-6 right-6 z-[210] p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
onClick={()=> setShowVideoModal(false)}
>
<SafeIcon icon={FiIcons.FiX} className="w-6 h-6" />
</button>
<div
className="relative w-[90vw] h-[85vh] max-w-5xl"
onClick={(e)=> e.stopPropagation()}
>
<iframe
src={demo.videoUrl}
className="w-full h-full rounded-xl shadow-2xl"
allow="accelerometer;autoplay;clipboard-write;encrypted-media;gyroscope;picture-in-picture;fullscreen"
allowFullScreen
title="Demo Video"
></iframe>
</div>
</div>
)}
</>
);
}